import { useCallback, useState } from 'react';
import { Center, Tabs, Text } from '@mantine/core';
import CustomSpinner from '@/components/Spinner.tsx';
import StyledTextInput from '@/components/StyledInputFields/StyledTextInput.tsx';
import useRunGroupsByTriggerDatasets
  from '@/pages/AutomationPage/components/ManualRunModal/hooks/useRunGroupsByTriggerDatasets.ts';
import { RunWithDatasets } from '@/types/runs.ts';
import PreviousRunGroupList
  from '@/pages/AutomationPage/components/ManualRunModal/components/PreviousRunUI/PreviousRunGroupList.tsx';
import { PreviousRunUIProps } from '@/pages/AutomationPage/components/ManualRunModal/components/PreviousRunUI/types.ts';

const PreviousRunUI = ({
  triggers,
  automationId,
  runName,
  setRunName,
  creatingRun,
  onSelectPerfectRun,
  onSelectPartialRun,
}: PreviousRunUIProps) => {
  const [selectedPreviousRunId, setSelectedPreviousRunId] = useState<string | undefined>(undefined);
  const [matchTab, setMatchTab] = useState<string>('perfect');

  const { perfectRunGroups, partialRunGroups, isLoadingRuns } =
    useRunGroupsByTriggerDatasets(triggers, automationId);

  const getRunUrl = useCallback(
    (runId: string) => `/automations/${automationId}/runs/${runId}`,
    [automationId],
  );

  const handlePerfectRunSelect = useCallback(
    (run: RunWithDatasets) => {
      setSelectedPreviousRunId(run.run.id);
      setRunName(run.run.name);
      onSelectPerfectRun(run);
    },
    [onSelectPerfectRun, setRunName],
  );

  const handlePartialRunSelect = useCallback(
    (run: RunWithDatasets) => {
      setRunName(run.run.name);
      onSelectPartialRun(run);
    },
    [onSelectPartialRun, setRunName],
  );

  if (isLoadingRuns) {
    return (
      <Center my={'xs'}>
        <CustomSpinner size={'md'} />
      </Center>
    );
  }

  if (perfectRunGroups.length === 0 && partialRunGroups.length === 0) {
    return (
      <Center>
        <Text>No previous runs to use</Text>
      </Center>
    );
  }

  return (
    <>
      <StyledTextInput
        label="Run Name"
        placeholder="Run name"
        size="sm"
        value={runName}
        onChange={(e) => setRunName(e.target.value)}
        autoFocus={true}
        data-autofocus
        required={true}
        readOnly={creatingRun}
      />

      <Tabs
        value={matchTab}
        onChange={(value) => value && setMatchTab(value)}
        color={'5E6AD2'}
        styles={{ tab: { borderBottomWidth: '1px' } }}
      >
        <Tabs.List>
          <Tabs.Tab value="perfect" c={matchTab === 'perfect' ? '#5E6AD2' : '#757575'}>
            Exact matches ({perfectRunGroups.length})
          </Tabs.Tab>
          <Tabs.Tab value="partial" c={matchTab === 'partial' ? '#5E6AD2' : '#757575'}>
            Partial matches ({partialRunGroups.length})
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="perfect" pt="md">
          {perfectRunGroups.length === 0 ? (
            <Center p="md">
              <Text size="12px" c="dimmed">
                No exact matches for the current trigger setup.
              </Text>
            </Center>
          ) : (
            <PreviousRunGroupList
              groups={perfectRunGroups}
              triggers={triggers}
              getRunUrl={getRunUrl}
              selectedPreviousRunId={selectedPreviousRunId}
              onSelectPerfectRun={handlePerfectRunSelect}
            />
          )}
        </Tabs.Panel>

        <Tabs.Panel value="partial" pt="md">
          {partialRunGroups.length === 0 ? (
            <Center p="md">
              <Text size="12px" c="dimmed">
                No partial matches for the current trigger setup.
              </Text>
            </Center>
          ) : (
            <PreviousRunGroupList
              groups={partialRunGroups}
              triggers={triggers}
              getRunUrl={getRunUrl}
              onSelectPartialRun={handlePartialRunSelect}
              isPartial={true}
            />
          )}
        </Tabs.Panel>
      </Tabs>
    </>
  );
};

export default PreviousRunUI;
